import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeftShort, ArrowRightShort } from 'react-bootstrap-icons';

import useDraggableScroll from '../../../hooks/use_draggable_scroll';

const CarouselItem = ({ caption, children }) => (
	<div className="carousel-item">
		{children}
		{caption && (
			<div className="carousel-caption-wrapper">
				<div className="carousel-caption xs">{caption}</div>
			</div>
		)}
	</div>
);

CarouselItem.propTypes = {
	caption: PropTypes.string,
	children: PropTypes.node.isRequired,
};

const Carousel = ({
	children,
	hideButtons = false,
	hideIndicators = false,
}) => {
	const ref = useRef(null);
	const scrollThreshold = 10;

	const [showLeftButton, setShowLeftButton] = useState(false);
	const [showRightButton, setShowRightButton] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);

	const { onMouseDown } = useDraggableScroll(ref, { direction: 'horizontal' });

	const handleScroll = () => {
		const container = ref.current;
		setShowLeftButton(container.scrollLeft > scrollThreshold);
		setShowRightButton(
			container.scrollWidth - container.clientWidth - container.scrollLeft >
				scrollThreshold,
		);
	};

	const handleScrollEnd = () => {
		const container = ref.current;
		const newIndex = Math.round(container.scrollLeft / container.clientWidth);
		setCurrentIndex(newIndex);
		container.scrollLeft = newIndex * container.clientWidth;
	};

	useEffect(() => {
		const container = ref.current;
		container.addEventListener('scroll', handleScroll);

		const debounceScrollEnd = () => {
			clearTimeout(container.scrollTimeout);
			container.scrollTimeout = setTimeout(handleScrollEnd, 100);
		};

		container.addEventListener('scroll', debounceScrollEnd);

		handleScroll(); // Initial check

		return () => {
			container.removeEventListener('scroll', handleScroll);
			container.removeEventListener('scroll', debounceScrollEnd);
			if (container.scrollTimeout) {
				clearTimeout(container.scrollTimeout);
			}
		};
	}, []);

	useEffect(() => {
		const handleResize = () => {
			handleScroll();
		};

		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const handleDotClick = (index) => {
		setCurrentIndex(index);
		const container = ref.current;
		container.scrollLeft = index * container.clientWidth;
	};

	const items = React.Children.toArray(children).filter(Boolean);

	return (
		<section className="carousel">
			{!hideButtons && showLeftButton && (
				<div className={`carousel-btn-container`}>
					<button
						className="btn text-white btn-circle btn-circle-lg btn-dark carousel-btn-left"
						onClick={() => {
							const container = ref.current;
							const newIndex = Math.max(currentIndex - 1, 0);
							container.scrollLeft = newIndex * container.clientWidth;
							setCurrentIndex(newIndex);
						}}
					>
						<ArrowLeftShort />
					</button>
				</div>
			)}

			<div className={`carousel-container`} onMouseDown={onMouseDown} ref={ref}>
				{items.map((child, index) => (
					<React.Fragment key={index}>{child}</React.Fragment>
				))}
			</div>

			{!hideButtons && showRightButton && (
				<div className={`carousel-btn-container`}>
					<button
						className="carousel-btn-right btn text-white btn-circle btn-circle-lg btn-dark"
						onClick={() => {
							const container = ref.current;
							const newIndex = Math.min(currentIndex + 1, items.length - 1);
							container.scrollLeft = newIndex * container.clientWidth;
							setCurrentIndex(newIndex);
						}}
					>
						<ArrowRightShort />
					</button>
				</div>
			)}

			{!hideIndicators && (
				<div className="carousel-indicators">
					{items.map((_, index) => (
						<span
							className={`carousel-indicator ${index === currentIndex ? 'active' : ''}`}
							key={index}
							onClick={() => handleDotClick(index)}
						>
							{index + 1}/{items.length}
						</span>
					))}
				</div>
			)}
		</section>
	);
};

Carousel.Item = CarouselItem;

Carousel.propTypes = {
	children: PropTypes.node.isRequired,
	hideButtons: PropTypes.bool,
	hideIndicators: PropTypes.bool,
};

export default Carousel;
